import { Component, computed, input, OnInit } from '@angular/core';
import { AbstractInputComponent } from '../abstract-input.component';

import { MatError } from '@angular/material/input';
import { MatOption, MatSelect, MatSelectTrigger } from '@angular/material/select';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { TranslocoPipe } from '@jsverse/transloco';
import { MatOptionSelectionChange } from '@angular/material/core';
import { MatFormField } from '@angular/material/form-field';
import { TranslateJoinPipe } from '../../../../../modules/pickups-hotel/pipes/translate-join.pipe';

export interface Option {
  label: string | null;
  value: any;
  icon?: string | null;
}

function toOptions(options: (string | Option | null)[] | null | undefined): Option[] {
  if (!options || options.length === 0) {
    return [];
  }
  return options.map((item): Option => {
    if (item === null) {
      return { label: null, value: null, icon: null };
    } else if (typeof item === 'string') {
      return { label: null, value: item, icon: null };
    } else if (typeof item === 'object' && 'value' in item) {
      return {
        label: item.label ?? item.value, // fallback au besoin
        value: item.value,
        icon: 'icon' in item ? item.icon : null,
      };
    } else {
      return { label: null, value: item, icon: null };
    }
  });
}

@Component({
  selector: 'logpn-input-select',
  templateUrl: './input-select.component.html',
  styleUrls: ['./input-select.component.scss'],
  imports: [
    MatFormField,
    MatSelect,
    FormsModule,
    ReactiveFormsModule,
    MatSelectTrigger,
    MatOption,
    MatError,
    TranslocoPipe,
    TranslateJoinPipe,
  ],
})
export class InputSelectComponent extends AbstractInputComponent implements OnInit {
  readonly multiple = input(false);
  readonly options = input<Option[], (string | Option | null)[] | null | undefined>([], {
    transform: toOptions,
  });
  readonly ALL_VALUE = '*';
  readonly panelClass = input<string | string[] | Set<string> | Record<string, unknown>>([]);
  readonly selectionOptions = computed(() => {
    if (!this.multiple()) {
      return this.options();
    }
    return [
      {
        label: 'forms.allOptions',
        value: this.ALL_VALUE,
      },
      ...this.options(),
    ];
  });

  selectedOptions(): Option[] {
    const values: unknown[] = this._control.value ?? [];
    return this.options().filter((o) => values.includes(o.value));
  }

  onOptionClicked(event: MatOptionSelectionChange) {
    if (!event.isUserInput) return;

    const clicked = event.source.value;
    const selected = this._control.value ?? [];
    const allValues = this.selectionOptions().map((option) => option.value);

    if (clicked === this.ALL_VALUE) {
      if (event.source.selected) {
        this._control.setValue([...allValues]);
      } else {
        this._control.setValue([]);
      }
      return;
    }

    if (event.source.selected) {
      const newSelection = [...selected, clicked];
      if (newSelection.length === this.options().length) {
        this._control.setValue([...allValues]);
      } else {
        this._control.setValue(newSelection);
      }
    } else {
      let newSelection = selected.filter((v: string) => v !== clicked);
      newSelection = newSelection.filter((v: string) => v !== this.ALL_VALUE);
      this._control.setValue(newSelection);
    }
  }

  ngOnInit(): void {
    if (!this.multiple()) {
      return;
    }
    const allExceptAll = this.options()
      .filter((o) => o.value !== this.ALL_VALUE)
      .map((o) => o.value);

    let initialSelection: string[];
    if (this._control.value.length === 0 || this._control.value.length === allExceptAll.length) {
      initialSelection = [this.ALL_VALUE, ...allExceptAll];
    } else {
      initialSelection = [...this._control.value];
    }
    this._control.setValue(initialSelection);
  }
}
